import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Banner, Button, Card, OtpInput } from '@cowoo/shared/ui-web';
import { authErrorKey, handleAuthResult, mapAuthError, totpSchema, type AuthErrorKind } from '@cowoo/shared';
import { STALE_TIME, qk } from '@cowoo/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { auth } from '../../lib/auth';
import { afterAuthentication } from './afterAuth';
import { readPendingLogin, rememberDetour } from './pending';

/**
 * `/login/totp` (06 § 5.3, reached from `/login/code`).
 *
 * Members normally have no TOTP; backoffice users who also use the member app
 * do (D-034). A recovery code is eight alphanumeric characters against the same
 * endpoint, so it is the same input with a longer length rather than a second
 * screen (`llm.txt` § 4, TOTP).
 */
export function LoginTotp() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pending = readPendingLogin();
  const detour = pending?.detour;

  const [code, setCode] = useState('');
  const [error, setError] = useState<AuthErrorKind | null>(null);
  const [verifying, setVerifying] = useState(false);
  // `afterAuthentication` clears the pending login; without this the guard
  // below would bounce a signed-in user back to `/login`.
  const succeeded = useRef(false);
  const [recovery, setRecovery] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (succeeded.current) return;
    if (!detour || detour.kind !== 'totp_required') navigate('/login', { replace: true });
  }, [detour, navigate]);

  const length = recovery ? 8 : 6;

  async function verify(entered: string): Promise<void> {
    if (!detour || verifying) return;
    if (!totpSchema.safeParse(entered).success || entered.length !== length) {
      setError('totp_invalid');
      return;
    }

    setVerifying(true);
    setError(null);
    try {
      const state = handleAuthResult(await auth.submitCode(detour, entered));
      // A detour answered by another detour: hand it on rather than assuming
      // this was the last step (04 § 11.2).
      if (state.status === 'challengeRequired') {
        rememberDetour({ kind: 'challenge', token: state.token });
        navigate('/login/challenge');
        return;
      }
      if (state.status === 'authenticated') {
        succeeded.current = true;
        navigate(await afterAuthentication(state), { replace: true });
      }
    } catch (thrown) {
      setError(mapAuthError(thrown, 'totp'));
      setCode('');
    } finally {
      setVerifying(false);
    }
  }

  const key = error ? authErrorKey(error) : null;

  return (
    <main className="mx-auto max-w-md px-5 py-20">
      <Link to="/login" className="font-body text-muted mb-6 inline-block text-sm underline">
        {t('common.back')}
      </Link>
      <h1 className="font-heading text-ink mb-2 text-3xl">{t('member.totp.title')}</h1>
      <p className="font-body text-text mb-6">
        {recovery ? t('member.totp.recoveryBody') : t('member.totp.body')}
      </p>
      <Card>
        <div className="flex flex-col gap-4">
          <OtpInput
            length={length}
            label={recovery ? t('member.totp.recoveryCode') : t('member.totp.code')}
            value={code}
            onChange={setCode}
            onComplete={(entered) => void verify(entered)}
            error={key ? t(key) : null}
            disabled={verifying}
          />
          <Button onClick={() => void verify(code)} loading={verifying} disabled={code.length < length} full>
            {t('member.totp.submit')}
          </Button>

          {recovery ? null : (
            <button
              type="button"
              onClick={() => {
                setRecovery(true);
                setCode('');
                setError(null);
              }}
              className="font-body text-muted text-sm underline"
            >
              {t('member.totp.useRecovery')}
            </button>
          )}

          <button
            type="button"
            onClick={() => setShowHelp((open) => !open)}
            className="font-body text-muted text-sm underline"
          >
            {t('member.totp.noAccess')}
          </button>
          {showHelp ? <SupportLine /> : null}
        </div>
      </Card>
    </main>
  );
}

/** The support contact of § 5.3 — from `/public/site`, never a constant. */
function SupportLine() {
  const { t } = useTranslation();
  const site = useQuery({
    queryKey: qk.public.site(),
    staleTime: STALE_TIME.publicSite,
    queryFn: async () => {
      const { data, error } = await api.GET('/public/site');
      if (error || !data) throw error ?? new Error('site unavailable');
      return data;
    },
  });

  const phone = site.data?.support.phone;
  const email = site.data?.support.email;
  if (!phone && !email) return null;

  return <Banner tone="info">{t('member.totp.noAccessBody', { phone, email })}</Banner>;
}
